import type { AnalyticsBreakdown, CommercialMetricId } from '~/types/leLens';

import {
  ACCOMMODATION_MODEL_IDS,
  ACCOMMODATION_MODEL_LABELS,
  type AccommodationModelId,
  COMMERCIAL_VIEW_PARAM_KEYS,
  type CommercialColumnId,
  type CommercialRevenueBasis,
  decodeState,
  metricForBasis,
} from './analyticsState';

export type { AccommodationModelId } from './analyticsState';

export const COMMERCIAL_ANALYSIS_PARAM = 'analysis';
export const ACCOMMODATION_MODEL_PARAM = 'accommodationModel';

export type CommercialAnalysisGroupId = 'products' | 'metrics' | 'dimensions';

export type CommercialAnalysisId =
  | 'overview'
  | 'product-accommodation'
  | 'product-bedbank'
  | 'product-flights'
  | 'product-tours'
  | 'product-cruise'
  | 'product-experiences'
  | 'metric-sales-orders'
  | 'metric-gross-margin'
  | 'dimension-market'
  | 'dimension-channel';

export type CommercialNavigationPreset = {
  id: CommercialAnalysisId;
  label: string;
  title: string;
  description: string;
  groupId: CommercialAnalysisGroupId | null;
  product?: string | ReadonlyArray<string>;
  focusMetric: CommercialMetricId;
  split: AnalyticsBreakdown;
  columns?: ReadonlyArray<CommercialColumnId>;
};

export type CommercialNavigationGroup = {
  id: CommercialAnalysisGroupId;
  label: string;
  items: ReadonlyArray<CommercialNavigationPreset>;
};

export type AccommodationNavigationItem = {
  kind: 'leaf';
  id: Extract<AccommodationModelId, 'flash' | 'lpp' | 'lpc' | 'nha'>;
  label: string;
};

/**
 * Accommodation stays one analysis page. These are scopes within that page, deliberately not
 * four governed model scopes: a copied leaf link changes only the Accommodation model filter.
 */
export const ACCOMMODATION_NAVIGATION_ITEMS: ReadonlyArray<AccommodationNavigationItem> = [
  { kind: 'leaf', id: 'flash', label: 'Flash' },
  { kind: 'leaf', id: 'lpp', label: 'LPP' },
  { kind: 'leaf', id: 'lpc', label: 'LPC' },
  { kind: 'leaf', id: 'nha', label: 'NHA' },
];

const ACCOMMODATION_MODEL_PAGE_COPY: Record<AccommodationNavigationItem['id'], { title: string; description: string }> =
  {
    flash: {
      title: 'Limited Time Lux Exclusive | Flash',
      description: 'Sales, margin and bookings for time-limited hotel offers.',
    },
    lpp: {
      title: 'Lux Property Partners | LPP',
      description: 'Sales, margin and bookings for partner-property offers.',
    },
    lpc: {
      title: 'Lux Premium Collection | LPC',
      description: 'Sales, margin and bookings for curated premium properties.',
    },
    nha: {
      title: 'Non-Hotel Accommodation | NHA',
      description: 'Sales, margin and bookings across non-hotel accommodation.',
    },
  };

/** The selected model is the page identity, not merely a filter chip. */
export function accommodationModelPageIdentity(
  model: AccommodationModelId | null | undefined,
): { eyebrow: 'Accommodation'; title: string; description: string } | null {
  if (!model || !(model in ACCOMMODATION_MODEL_PAGE_COPY)) {
    return null;
  }

  return {
    eyebrow: 'Accommodation',
    ...ACCOMMODATION_MODEL_PAGE_COPY[model as AccommodationNavigationItem['id']],
  };
}

/** What a product or model page is, as opposed to what the reader refined: these scope the page but are not filters. */
export type PageIdentity = { product: boolean; model: boolean };

export function pageIdentity(
  analysisId: CommercialAnalysisId,
  filters: { product: ReadonlyArray<string>; accommodationModel?: ReadonlyArray<AccommodationModelId> },
): PageIdentity {
  const presetProduct = PRESET_BY_ID.get(analysisId)?.product;
  const product =
    typeof presetProduct === 'string' && filters.product.length === 1 && filters.product[0] === presetProduct;
  const model =
    analysisId === 'product-accommodation' &&
    product &&
    filters.accommodationModel?.length === 1 &&
    accommodationModelPageIdentity(filters.accommodationModel[0]) !== null;
  return { product, model };
}

/** The filters the reader chose: the page's own product and model removed. */
export function refineFilters<T extends { product: Array<string>; accommodationModel?: Array<AccommodationModelId> }>(
  filters: T,
  identity: PageIdentity,
): T {
  return {
    ...filters,
    product: identity.product ? [] : filters.product,
    accommodationModel: identity.model ? undefined : filters.accommodationModel,
  };
}

/** The page above this one, for the eyebrow's Back link: a model's product, or Commercial for a product page. */
export function parentAnalysis(
  analysisId: CommercialAnalysisId,
  identity: PageIdentity,
): { id: CommercialAnalysisId; label: string } | null {
  if (identity.model) {
    return { id: 'product-accommodation', label: 'Accommodation' };
  }
  return analysisId.startsWith('product-') ? { id: 'overview', label: 'Commercial' } : null;
}

/** The strip's name: "LPC performance", "Flights performance", "Commercial performance". */
export function analysisStripTitle(analysisId: CommercialAnalysisId, model: AccommodationModelId | null): string {
  if (model) {
    return `${ACCOMMODATION_MODEL_LABELS[model]} performance`;
  }
  return `${analysisId.startsWith('product-') ? commercialNavigationPreset(analysisId).title : 'Commercial'} performance`;
}

export const COMMERCIAL_OVERVIEW: CommercialNavigationPreset = {
  id: 'overview',
  label: 'Overview',
  title: 'Commercial',
  description:
    'See whether growth is translating into margin, with orders and refunds across every product and market.',
  groupId: null,
  focusMetric: 'commercial.ttv_br',
  split: 'product',
};

export const COMMERCIAL_NAVIGATION_GROUPS: ReadonlyArray<CommercialNavigationGroup> = [
  {
    id: 'products',
    label: 'Products',
    items: [
      {
        id: 'product-accommodation',
        label: 'Accommodation',
        title: 'Accommodation',
        description: 'See whether Accommodation is growing profitably, then compare Flash, LPP, LPC and NHA.',
        groupId: 'products',
        product: 'accommodation',
        focusMetric: 'commercial.ttv_br',
        split: 'accommodation_type',
        columns: ['gm', 'orders', 'aov'],
      },
      {
        id: 'product-flights',
        label: 'Flights',
        title: 'Flights',
        description:
          'See whether Flights is growing profitably and how domestic and international performance differs.',
        groupId: 'products',
        product: 'flights',
        focusMetric: 'commercial.ttv_br',
        split: 'domestic_international',
        columns: ['gm', 'orders', 'aov'],
      },
      {
        id: 'product-tours',
        label: 'Tours',
        title: 'Tours',
        description: 'See whether Tours is growing profitably and which customer markets are driving the result.',
        groupId: 'products',
        product: 'tours',
        focusMetric: 'commercial.ttv_br',
        split: 'customer_country',
        columns: ['gm', 'orders', 'aov'],
      },
      {
        id: 'product-cruise',
        label: 'Cruise',
        title: 'Cruise',
        description: 'See whether Cruise is growing profitably and which booking markets are driving the result.',
        groupId: 'products',
        product: 'cruise',
        focusMetric: 'commercial.ttv_br',
        split: 'market',
        columns: ['gm', 'orders', 'aov'],
      },
      {
        id: 'product-experiences',
        label: 'Experiences',
        title: 'Experiences',
        description:
          'See whether Experiences is growing profitably across Lux Everyday, Klook and other governed providers.',
        groupId: 'products',
        product: 'experiences',
        focusMetric: 'commercial.ttv_br',
        split: 'platform',
        columns: ['gm', 'orders', 'aov'],
      },
    ],
  },
];

// Kept as route-compatible presets for shared links created before the navigation was simplified.
// Bedbank is represented by LPP/LPP NHA inside Accommodation, metrics remain selectable in the
// headline strip, and dimensions in the breakdown tabs, so these destinations no longer need to
// consume permanent sidebar space.
const LEGACY_PRESETS: ReadonlyArray<CommercialNavigationPreset> = [
  {
    id: 'product-bedbank',
    label: 'Bedbank',
    title: 'Bedbank',
    description: 'Understand Bedbank performance across destination regions.',
    groupId: 'products',
    product: 'bedbank',
    focusMetric: 'commercial.ttv_br',
    split: 'destination_region',
    columns: ['gm', 'orders', 'aov'],
  },
  {
    id: 'metric-sales-orders',
    label: 'Sales & orders',
    title: 'Sales & orders',
    description: 'Track sales volume, order movement and average order value across products.',
    groupId: 'metrics',
    focusMetric: 'commercial.ttv_br',
    split: 'product',
    columns: ['orders', 'aov'],
  },
  {
    id: 'metric-gross-margin',
    label: 'Gross margin',
    title: 'Gross margin',
    description: 'Track margin dollars, margin rate and refund impact across products.',
    groupId: 'metrics',
    focusMetric: 'commercial.gross_margin_br',
    split: 'product',
    columns: ['margin_pct', 'refund_rate'],
  },
  {
    id: 'dimension-market',
    label: 'Market',
    title: 'Commercial by market',
    description: 'Compare performance and identify the markets driving change.',
    groupId: 'dimensions',
    focusMetric: 'commercial.ttv_br',
    split: 'market',
    columns: ['gm', 'orders'],
  },
  {
    id: 'dimension-channel',
    label: 'Channel',
    title: 'Commercial by channel',
    description: 'Compare performance and identify the sales channels driving change.',
    groupId: 'dimensions',
    focusMetric: 'commercial.ttv_br',
    split: 'sale_channel',
    columns: ['gm', 'orders'],
  },
];

const PRESETS: ReadonlyArray<CommercialNavigationPreset> = [
  COMMERCIAL_OVERVIEW,
  ...COMMERCIAL_NAVIGATION_GROUPS.flatMap((group) => group.items),
  ...LEGACY_PRESETS,
];

const PRESET_BY_ID = new Map(PRESETS.map((preset) => [preset.id, preset]));

const PRESERVED_VIEW_PARAMS = new Set(['basis', 'period', 'compare', 'from', 'to', 'cfrom', 'cto']);

function arraysMatch(left: ReadonlyArray<string>, right: ReadonlyArray<string>): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

/**
 * Older shared links predate `analysis`. Keep the navigation rail coherent when their existing
 * page scope describes one of the new destinations; product pages remain the same destination
 * when the reader has chosen a different breakdown within that product.
 */
function legacyAnalysisFromParams(params: URLSearchParams): CommercialAnalysisId {
  const state = decodeState(params);
  const matchingPreset = PRESETS.find((preset) => {
    const expectedProduct = preset.product
      ? typeof preset.product === 'string'
        ? [preset.product]
        : preset.product
      : [];
    return (
      arraysMatch(state.filters.product, expectedProduct) &&
      state.filters.market.length === 0 &&
      state.filters.saleChannel.length === 0 &&
      state.focusMetric === metricForBasis(preset.focusMetric, state.basis) &&
      (preset.groupId === 'products' || state.split === preset.split) &&
      arraysMatch(state.columns, preset.columns ?? [])
    );
  });

  return matchingPreset?.id ?? COMMERCIAL_OVERVIEW.id;
}

export function commercialAnalysisFromParams(params: URLSearchParams): CommercialAnalysisId {
  const requested = params.get(COMMERCIAL_ANALYSIS_PARAM) as CommercialAnalysisId | null;
  if (requested) {
    return PRESET_BY_ID.has(requested) ? requested : COMMERCIAL_OVERVIEW.id;
  }
  return legacyAnalysisFromParams(params);
}

export function commercialAnalysisGroup(id: CommercialAnalysisId): CommercialAnalysisGroupId | null {
  return PRESET_BY_ID.get(id)?.groupId ?? null;
}

export function commercialNavigationPreset(id: CommercialAnalysisId): CommercialNavigationPreset {
  return PRESET_BY_ID.get(id) ?? COMMERCIAL_OVERVIEW;
}

/**
 * Applies one Commercial navigation destination as a complete, shareable page preset.
 * Reporting period, comparison and BR/AR are reader choices and survive. Scope, focus,
 * breakdown and chart presentation are reset so a previous destination cannot leak into this one.
 */
export function applyCommercialAnalysis(current: URLSearchParams, id: CommercialAnalysisId): URLSearchParams {
  const preset = PRESET_BY_ID.get(id) ?? COMMERCIAL_OVERVIEW;
  const next = new URLSearchParams(current);
  const basis: CommercialRevenueBasis = next.get('basis') === 'ar' ? 'ar' : 'br';

  for (const key of COMMERCIAL_VIEW_PARAM_KEYS) {
    if (!PRESERVED_VIEW_PARAMS.has(key)) {
      next.delete(key);
    }
  }

  next.set(COMMERCIAL_ANALYSIS_PARAM, preset.id);
  if (preset.product) {
    next.set('product', typeof preset.product === 'string' ? preset.product : preset.product.join(','));
  }

  const focusMetric = metricForBasis(preset.focusMetric, basis);
  // Match analyticsState's clean default URL convention while keeping AR explicit.
  if (focusMetric === 'commercial.ttv_br') {
    next.delete('focus');
  } else {
    next.set('focus', focusMetric);
  }

  if (preset.split === 'product') {
    next.delete('split');
  } else {
    next.set('split', preset.split);
  }

  if (preset.columns?.length) {
    next.set('cols', preset.columns.join(','));
  }

  return next;
}

/** Reads one valid model from a shared URL. Multiple model values remain supported by state/API, but rail leaves are singular. */
export function accommodationModelFromParams(params: URLSearchParams): AccommodationModelId | null {
  if (decodeState(params).filters.product[0] !== 'accommodation') {
    return null;
  }
  const requested = params.get(ACCOMMODATION_MODEL_PARAM)?.split(',')[0];
  return (ACCOMMODATION_MODEL_IDS as ReadonlyArray<string>).includes(requested ?? '')
    ? (requested as AccommodationModelId)
    : null;
}

/**
 * Opens the existing Accommodation analysis page with one model scope. Reporting choices survive
 * via `applyCommercialAnalysis`; stale market/channel/split presentation state cannot leak in.
 */
export function applyAccommodationModelNavigation(
  current: URLSearchParams,
  model: AccommodationModelId,
): URLSearchParams {
  const next = applyCommercialAnalysis(current, 'product-accommodation');
  next.set(ACCOMMODATION_MODEL_PARAM, model);
  // Accommodation type is hidden on a model page, so open on a split that stays.
  next.set('split', 'market');
  return next;
}

/** Bedbank left the navigation in favour of the Accommodation page. Preserve old bookmarks without a sixth product view. */
export function canonicalCommercialAnalysis(current: URLSearchParams): URLSearchParams {
  if (current.get(COMMERCIAL_ANALYSIS_PARAM) !== 'product-bedbank') {
    return new URLSearchParams(current);
  }
  return applyCommercialAnalysis(current, 'product-accommodation');
}
